import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { http } from "../../lib/http";
import { AlertCircle, Lock } from "lucide-react";

export default function PayNowModal({ invoice, open, onClose, onPaid }) {
  const { t } = useTranslation();
  const [card, setCard] = useState("4242 4242 4242 4242");
  const [exp, setExp] = useState("12/29");
  const [cvc, setCvc] = useState("123");
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      const intent = (await http.post("/payments/intent", { invoice_id: invoice.id })).data;
      const confirm = (await http.post("/payments/confirm", {
        intent_id: intent.intent_id, card_number: card.replace(/\s/g, ""),
      })).data;
      if (confirm.status === "succeeded") {
        toast.success(t("payments.success"));
        onPaid && onPaid(); onClose();
      } else {
        toast.error(t("payments.failed"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.hint || e?.response?.data?.detail?.error || "Payment failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4 text-emerald-600"/>{t("payments.payInvoice")} · {invoice?.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs p-2 rounded flex items-center gap-2" data-testid="payments-mock-banner">
          <AlertCircle className="h-4 w-4 shrink-0"/>
          <span><strong>{t("payments.mockedBanner")}</strong> — {t("payments.mockedDescription")}</span>
        </div>
        <div className="space-y-2 mt-2">
          <div>
            <label className="text-xs">{t("payments.cardNumber")}</label>
            <Input value={card} onChange={(e)=>setCard(e.target.value)} placeholder="4242 4242 4242 4242" data-testid="payments-card-number"/>
          </div>
          <div className="flex gap-2">
            <div className="flex-1"><label className="text-xs">{t("payments.expiry")}</label><Input value={exp} onChange={(e)=>setExp(e.target.value)} placeholder="MM/YY"/></div>
            <div className="flex-1"><label className="text-xs">CVC</label><Input value={cvc} onChange={(e)=>setCvc(e.target.value)} placeholder="123"/></div>
          </div>
          <div className="text-center text-lg font-semibold text-[#0A1F44] pt-2" data-testid="payments-modal-total">
            {t("invoices.total")}: <span className="text-[#C8102E]">
              {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(invoice?.total || 0)}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={pay} disabled={loading} className="bg-[#C8102E] hover:bg-[#a40c25]" data-testid="payments-pay-confirm">
            {loading ? "…" : t("payments.payNow")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
